/**
 * Embedded Maestro chat for Create Media Plan (and similar pages).
 *
 * Decomposition note (PR-D2): the original 1,577-line MaestroChatPanel.tsx was
 * decomposed per gist I19 into this directory:
 *   - `utils/payloadParsers.ts`  — pure SSE / model-output parsers
 *   - `utils/phases.ts`           — pipeline section data + helpers
 *   - `components/MarkdownMessage.tsx` — inline markdown renderer
 *   - `index.tsx`                 — this file; the forwardRef orchestration component
 *
 * TODO(ORCH-179): index.tsx is still ~1,400 lines. The remaining decomposition
 * is tracked at https://linear.app/orchestratehq/issue/ORCH-179 — split into
 * `hooks/useChatStream.ts`, `hooks/usePlanPipeline.ts`, `hooks/useApprovalFlow.ts`,
 * `components/ChatTranscript.tsx`, `components/PhaseAccordion.tsx`. Deferred from
 * PR #64 / PR #68 because the state extraction requires live-LLM end-to-end
 * verification (no CI provider keys).
 *
 * With a planning entity or media brief: orchestrator (mode=plan), approval at each gate,
 * full pipeline after brief. Otherwise: ReAct draft assist (mode=chat). Wires flight
 * timeline POST, media mixer, approvals.
 *
 * The panel is step-focused (ORCH-427): the step header, the field chips and the
 * active field's card, with no free-text "Ask Maestro" composer beneath them. Typed
 * answers go through each field card's own control (`MaestroMappingsStepper`);
 * a general ask-anything chat is a separate surface (ORCH-428). Chat mode
 * (`planningEntityId` absent) therefore has no live entry point today — its
 * branches remain for that separate feature to reuse.
 *
 * Plan-mode orchestrator streams render as accordion sections per workflow
 * step of `wf_media-planning` (CoT, tools, output). Rows come from the fetched
 * steps and each event's `step_id` (ORCH-286 §5.9), so both phases of a step —
 * and a parallel step's sub-agents — append into that step's own row, and a
 * step added in the Workflow Manager renders with no change here.
 */

import React, { forwardRef, useImperativeHandle } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import maestroChatService from '../../../services/maestroChatService';
import workflowService from '../../../services/workflowService';
import {
  ApprovalRequiredEvent,
  ChatMessage,
  ChatModelOption,
  FlightTimelineBuildResponse,
  LlmGenerateRequest,
  LlmGenerateResponse,
  ModelOutput,
  ToolCallEvent,
} from '../../../types/chat';
import ApprovalCard, { type OrchestratorResumeDetail } from '../ApprovalCard';
import ToolCallPanel from '../ToolCallPanel';
import { brandColors } from '../../../theme/orchestrateTheme';
import MediaMixerDrawer from './components/MediaMixerDrawer';
import { mergeFlightTimelineRequest } from '../../../utils/flightTimelineMerge';

// PR-D2 decomposition: helpers extracted to utils/ and components/.
import MarkdownMessage from './components/MarkdownMessage';
import {
  pickPrimaryModelOutput,
  sanitizeCoTDisplay,
  parseApprovalRequiredPayload,
  consumeUiAction,
} from './utils/payloadParsers';
import {
  PLAN_WORKFLOW_ID,
  SKIP_PHASES,
  PhaseSection,
  PlanStepShape,
  StepEventRef,
  applyPipelineComplete,
  buildPlanPipelineFromSteps,
  firstNonEngineStep,
  formIntakeStep,
  PROGRESS_START_LABEL,
  progressLabel,
  phaseLabel,
  sectionKeyForEvent,
  stepIndexForKey,
  unlockFirstStep,
} from './utils/phases';
import MaestroMappingsStepper, {
  CREATE_PLAN_FIELD_ORDER,
  computeExtractionSummary,
  getFieldStepStatus,
  hasUserValue,
} from '../MaestroMappingsStepper';
import MaestroWorkflowStepper from '../MaestroWorkflowStepper';
import { maestroSurfaceSx } from '../maestroSurface';
import { useCreatePlanFieldLabels } from '../../../hooks/useModelFieldLabels';
import { DismissibleBanner } from '../../common/DismissibleBanner';

/**
 * Gap between the header group (title row + dot rail) and whatever comes
 * next — the chips, a question card, the progress line — so the two read as
 * separate groups (ORCH-426). Clearly larger than the 8px inside the header.
 */
const HEADER_TO_CONTENT_GAP = '24px';

/** Product: hide inline agent tool-call chips (server-side tools still execute). */
const SHOW_AGENT_TOOL_CALLS_UI = false;

export type MaestroChatPanelHandle = {
  openMediaMixer: (initialAllocations?: Record<string, number>, reason?: string) => void;
  /** After Create Plan: run the brief review workflow step (stub) on the same orchestrator thread. */
  runBriefReviewAfterCreatePlan: () => Promise<void>;
};

/** Per-field match result shape from the Maestro backend (ORCH-215). */
export interface MaestroFieldResult {
  status: 'matched' | 'unmatched' | 'pending';
  confidence: number;
  source_term?: string;
  /** Human-readable MDM option label (e.g. "United Kingdom") set by the agent for list fields. */
  resolved_label?: string;
}

export interface MaestroChatPanelProps {
  planningEntityId?: string;
  onApplyCreatePlanFields?: (
    fields: Record<string, unknown>,
    fieldResults?: Record<string, MaestroFieldResult>
  ) => void;
  /** Called once when Maestro completes brief review (brief_review_complete handoff). Gates field indicator icons. */
  onBriefProcessed?: () => void;
  onFlightTimelineReady?: (data: FlightTimelineBuildResponse) => void;
  onMediaMixerOpen?: (action: {
    type: 'open_media_mixer';
    mode?: 'user_edit' | 'auto_apply';
    reason?: string;
    target?: 'current_timeline';
    allocations_percent?: Record<string, number>;
  }) => void;
  title?: string;
  sx?: SxProps<Theme>;
  resolveContinuePastBrief?: () => boolean;
  tenantId?: string;
  getCreatePlanFieldValue?: (fdKey: string) => unknown;
  /** Create Media Plan: form field fd_media-brief has file(s) attached */
  hasAttachedMediaBrief?: boolean;
}

const MaestroChatPanel = forwardRef<MaestroChatPanelHandle, MaestroChatPanelProps>(
  (
    {
      planningEntityId,
      onApplyCreatePlanFields,
      onBriefProcessed,
      onFlightTimelineReady,
      onMediaMixerOpen,
      resolveContinuePastBrief,
      tenantId = 'GLOBAL',
      getCreatePlanFieldValue,
      hasAttachedMediaBrief = false,
      title = 'Maestro',
      sx: rootCardSx,
    },
    ref
  ) => {
    /** Latest form-value reader, so the auto-advance effect can stay dependency-free. */
    const getCreatePlanFieldValueRef = React.useRef(getCreatePlanFieldValue);
    getCreatePlanFieldValueRef.current = getCreatePlanFieldValue;

    /**
     * Maestro's field steps in the form's order — the metadata view's display_order
     * (ORCH-405). Same cache entry the stepper sorts its chips with, so a step index
     * means the same field here and there. Ref'd for the dependency-free callbacks.
     */
    const { orderKeys: orderCreatePlanKeys } = useCreatePlanFieldLabels(tenantId);
    const createPlanFieldOrder = React.useMemo(
      () => orderCreatePlanKeys(CREATE_PLAN_FIELD_ORDER),
      [orderCreatePlanKeys]
    );
    const createPlanFieldOrderRef = React.useRef(createPlanFieldOrder);
    createPlanFieldOrderRef.current = createPlanFieldOrder;

    const [messages, setMessages] = React.useState<ChatMessage[]>([]);
    const [workflowStreamBusy, setWorkflowStreamBusy] = React.useState(false);
    /**
     * Separate from the orchestrator stream. Set by the side-chat path of
     * `executeGenerateStream`; still read for `briefPassRunning` and the progress
     * line (ORCH-406), which is why it outlived the composer (ORCH-427).
     */
    const [chatStreamBusy, setChatStreamBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [sessionId, setSessionId] = React.useState<string | null>(null);
    const [selectedModelKey, setSelectedModelKey] = React.useState<string | null>(null);
    const [availableModels, setAvailableModels] = React.useState<ChatModelOption[]>([]);
    const [toolCalls, setToolCalls] = React.useState<ToolCallEvent[]>([]);
    /** Active accordion section key: a workflow step_id in plan mode, an engine phase in chat mode. */
    const [currentSectionKey, setCurrentSectionKey] = React.useState('');
    const [activeStreamPlan, setActiveStreamPlan] = React.useState(false);
    const [streamConnected, setStreamConnected] = React.useState(false);
    const [timelineRendered, setTimelineRendered] = React.useState(false);
    const [budgetAllocationContext, setBudgetAllocationContext] = React.useState<
      LlmGenerateRequest['budget_allocation_context'] | undefined
    >();
    const [mixerOpen, setMixerOpen] = React.useState(false);
    const [mixerInitialAllocations, setMixerInitialAllocations] = React.useState<
      Record<string, number>
    >({});
    const [mixerReason, setMixerReason] = React.useState<string | undefined>();
    const [runId, setRunId] = React.useState<string | null>(null);
    const orchestratorThreadIdRef = React.useRef<string | null>(null);
    const [approvalQueue, setApprovalQueue] = React.useState<
      Array<{ key: string; data: ApprovalRequiredEvent }>
    >([]);
    const lastStreamWasPlanRef = React.useRef(false);
    /** One-shot auto-run after fd_media-brief attachment (draft PDF rehydrated server-side). */
    const briefAutoRunStartedRef = React.useRef(false);
    /** Tracks action_ids already applied this session so bundle replays are no-ops. */
    const appliedUiActionIdsRef = React.useRef<Set<string>>(new Set());

    /** Workflow Manager steps fetched for wf_media-planning (business lifecycle rows), sorted by sequence_order. */
    const planStepsRef = React.useRef<PlanStepShape[]>([]);
    const [planSteps, setPlanSteps] = React.useState<PlanStepShape[]>([]);
    const [stepsLoading, setStepsLoading] = React.useState(false);

    const [phaseSections, setPhaseSections] = React.useState<PhaseSection[]>([]);
    const currentSectionKeyRef = React.useRef<string>('');
    const streamingAssistantIdRef = React.useRef<string | null>(null);
    /** Completed accordions: user can reopen; keyed by section key. */
    const [openCompletedPhases, setOpenCompletedPhases] = React.useState<Set<string>>(new Set());
    /**
     * The Briefing step's progress line (ORCH-406): what the run is doing right now,
     * already resolved to display text. Empty until the first mapped stage or tool
     * arrives; reset whenever a plan run starts.
     */
    const [progressText, setProgressText] = React.useState('');
    /**
     * Plan-mode stepper: the step the user pinned by clicking its dot, together
     * with the engine step that was current when they did (ORCH-277). The pin
     * counts only while the engine is still on that step, so once the run moves
     * on the panel goes back to following it — derived at render rather than
     * cleared by an effect, which would race the engine's own state updates.
     */
    const [pinnedStep, setPinnedStep] = React.useState<{
      key: string;
      atSection: string;
    } | null>(null);

    /** Accumulated per-field match results from Maestro (ORCH-215/ORCH-256). */
    const [localFieldResults, setLocalFieldResults] = React.useState<
      Record<string, MaestroFieldResult>
    >({});
    const localFieldResultsRef = React.useRef<Record<string, MaestroFieldResult>>({});
    /** Active step index for the MaestroMappingsStepper (ORCH-256). */
    const [mappingsActiveStep, setMappingsActiveStep] = React.useState(0);
    /** True once Maestro's brief extraction handoff fires; gates the extraction quality banner (ORCH-265). */
    const [briefExtractionDone, setBriefExtractionDone] = React.useState(false);

    const extractionSummary = React.useMemo(
      () => (briefExtractionDone ? computeExtractionSummary(localFieldResults) : null),
      [briefExtractionDone, localFieldResults]
    );

    React.useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const catalog = await maestroChatService.listModels();
          if (cancelled) return;
          setAvailableModels(catalog.models || []);
          setSelectedModelKey(
            (prev) => prev || catalog.default_model || catalog.models?.[0]?.key || null
          );
        } catch {
          /* non-fatal */
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    /** Resolve the accordion section key an orchestrator event's content lands in. */
    const sectionKeyForStepEvent = React.useCallback(
      (event: StepEventRef): string => sectionKeyForEvent(event, planStepsRef.current),
      []
    );

    /** Make the workflow row `sectionKey` names the active one: earlier rows complete, later rows locked. */
    const syncPipelineToStep = React.useCallback((sectionKey: string) => {
      const steps = planStepsRef.current;
      const ai = stepIndexForKey(sectionKey, steps);
      if (ai < 0) return;

      setPhaseSections((prev) =>
        steps.map((step, idx) => {
          const existing = prev.find((s) => s.key === step.step_id);
          const base: PhaseSection = existing ?? {
            key: step.step_id,
            cot: '',
            toolCallIds: [],
            outputText: '',
            summaryMarkdown: undefined,
            completed: false,
            locked: false,
          };
          return { ...base, key: step.step_id, completed: idx < ai, locked: idx > ai };
        })
      );
      setCurrentSectionKey(sectionKey);
      currentSectionKeyRef.current = sectionKey;
    }, []);

    React.useEffect(() => {
      if (!planningEntityId) {
        setPhaseSections([]);
        setCurrentSectionKey('');
        currentSectionKeyRef.current = '';
        planStepsRef.current = [];
        setPlanSteps([]);
        setLocalFieldResults({});
        localFieldResultsRef.current = {};
        setMappingsActiveStep(0);
        setBriefExtractionDone(false);
        return;
      }
      // Reset transient state; pipeline is rebuilt when the fetch effect resolves.
      setPhaseSections([]);
      setCurrentSectionKey('');
      currentSectionKeyRef.current = '';
      setOpenCompletedPhases(new Set());
      setLocalFieldResults({});
      localFieldResultsRef.current = {};
      setMappingsActiveStep(0);
      setProgressText('');
      setBriefExtractionDone(false);
    }, [planningEntityId]);

    React.useEffect(() => {
      if (!planningEntityId) return;
      let cancelled = false;
      setStepsLoading(true);
      (async () => {
        try {
          const steps = await workflowService.getWorkflowSteps({
            workflow_id: PLAN_WORKFLOW_ID,
            status: 'active',
          });
          if (cancelled) return;
          const sorted = [...steps].sort((a, b) => a.sequence_order - b.sequence_order);
          planStepsRef.current = sorted;
          setPlanSteps(sorted);
          const sections = buildPlanPipelineFromSteps(sorted);
          setPhaseSections(sections);
        } catch {
          /* non-fatal — accordion stays empty during load failure */
        } finally {
          if (!cancelled) setStepsLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [planningEntityId]);

    /** Append a chat-mode section (no workflow row owns this event's phase). */
    const ensureChatSection = React.useCallback((key: string) => {
      if (SKIP_PHASES.has(key)) return;
      setPhaseSections((prev) => {
        const exists = prev.some((s) => s.key === key);
        if (exists) return prev;
        const closed = prev.map((s) => (s.completed ? s : { ...s, completed: true }));
        return [
          ...closed,
          {
            key,
            cot: '',
            toolCallIds: [],
            outputText: '',
            summaryMarkdown: undefined,
            completed: false,
            locked: false,
          },
        ];
      });
    }, []);

    /**
     * Route an orchestrator event onto the pipeline. Events that resolve to a
     * workflow row (every phase and sub-agent of a step does, via `step_id`)
     * sync completed/locked/active across the rows; anything else — chat mode,
     * or a phase no loaded step owns — keeps its own section, as before.
     */
    const handleStepEvent = React.useCallback(
      (event: StepEventRef) => {
        const phase = event.phase || '';
        // Connector / lifecycle phases never move the pipeline — the finalize
        // path still has a step bound, so this can't key off step_id alone.
        if (phase && SKIP_PHASES.has(phase)) return;
        const key = sectionKeyForStepEvent(event);
        if (!key || SKIP_PHASES.has(key)) return;
        if (stepIndexForKey(key, planStepsRef.current) >= 0) {
          syncPipelineToStep(key);
          return;
        }
        setCurrentSectionKey(key);
        if (key !== currentSectionKeyRef.current) {
          currentSectionKeyRef.current = key;
          ensureChatSection(key);
        }
      },
      [syncPipelineToStep, sectionKeyForStepEvent, ensureChatSection]
    );

    const appendPhaseCot = React.useCallback((key: string, text: string) => {
      if (SKIP_PHASES.has(key)) return;
      setPhaseSections((prev) =>
        prev.map((s) => (s.key === key ? { ...s, cot: s.cot + text } : s))
      );
    }, []);

    const appendPhaseOutput = React.useCallback((key: string, text: string) => {
      if (!key || SKIP_PHASES.has(key)) return;
      setPhaseSections((prev) =>
        prev.map((s) => (s.key === key ? { ...s, outputText: s.outputText + text } : s))
      );
    }, []);

    const addPhaseToolCall = React.useCallback((key: string, toolCallId: string) => {
      if (!SHOW_AGENT_TOOL_CALLS_UI || !key || SKIP_PHASES.has(key)) return;
      setPhaseSections((prev) =>
        prev.map((s) =>
          s.key === key && !s.toolCallIds.includes(toolCallId)
            ? { ...s, toolCallIds: [...s.toolCallIds, toolCallId] }
            : s
        )
      );
    }, []);

    /**
     * Pipeline finalized (plan approved): the AI rows complete, the first row
     * with no engine phases ('Plan Approval' in the seeded workflow) becomes
     * the active one, later lifecycle rows stay locked. Chat-mode sections
     * (no such row) complete everything, as before.
     */
    const finalizePlanPipeline = React.useCallback(() => {
      const steps = planStepsRef.current;
      setPhaseSections((prev) => applyPipelineComplete(prev, steps));
      const handoverStep = firstNonEngineStep(steps);
      if (handoverStep) {
        setCurrentSectionKey(handoverStep.step_id);
        currentSectionKeyRef.current = handoverStep.step_id;
        setStreamConnected(false);
      }
    }, []);

    const applyIntakeHandoff = React.useCallback((lr: LlmGenerateResponse) => {
      const h = lr.workflow_ui_handoff;
      const sm = (h?.summary_markdown || lr.final_answer || '').trim();
      // Intake belongs to the form_intake step's row, whatever it is labelled.
      const intakeKey = formIntakeStep(planStepsRef.current)?.step_id ?? '';
      setPhaseSections((prev) => {
        const intakeIdx = prev.findIndex((s) => s.key === intakeKey);
        return prev.map((s, idx) => {
          if (s.key === intakeKey) {
            return {
              ...s,
              completed: true,
              locked: false,
              cot: '',
              outputText: '',
              summaryMarkdown: sm,
            };
          }
          if (intakeIdx >= 0 && idx > intakeIdx) {
            return { ...s, locked: true, completed: false };
          }
          return s;
        });
      });
      setCurrentSectionKey(intakeKey);
      currentSectionKeyRef.current = intakeKey;
      setOpenCompletedPhases(new Set());
      setBriefExtractionDone(true);
    }, []);

    const applyBriefReviewStubHandoff = React.useCallback((lr: LlmGenerateResponse) => {
      const sm = (lr.workflow_ui_handoff?.summary_markdown || lr.final_answer || '').trim();
      // Same form_intake row as brief intake; the review summary supersedes.
      const reviewKey = formIntakeStep(planStepsRef.current)?.step_id ?? '';
      setPhaseSections((prev) => {
        const reviewIdx = prev.findIndex((s) => s.key === reviewKey);
        return prev.map((s, idx) => {
          if (s.key === reviewKey) {
            return {
              ...s,
              completed: true,
              locked: false,
              cot: '',
              outputText: '',
              summaryMarkdown: sm || s.summaryMarkdown,
            };
          }
          if (reviewIdx >= 0 && idx < reviewIdx) {
            return { ...s, locked: false, completed: true };
          }
          if (reviewIdx >= 0 && idx > reviewIdx) {
            return { ...s, locked: true, completed: false };
          }
          return s;
        });
      });
      setCurrentSectionKey(reviewKey);
      currentSectionKeyRef.current = reviewKey;
      setOpenCompletedPhases(new Set([reviewKey]));
      setBriefExtractionDone(true);
    }, []);

    /**
     * Merge new Maestro field results into local state and auto-advance the
     * MaestroMappingsStepper past high-confidence resolved fields (ORCH-256)
     * and fields the user has already filled in themselves (ORCH-380) — neither
     * needs the user's attention, so the wizard shouldn't park on them.
     */
    const applyFieldResults = React.useCallback(
      (newResults: Record<string, MaestroFieldResult> | undefined) => {
        if (!newResults) return;
        const merged = { ...localFieldResultsRef.current, ...newResults };
        localFieldResultsRef.current = merged;
        setLocalFieldResults(merged);
        setMappingsActiveStep((current) => {
          const order = createPlanFieldOrderRef.current;
          let next = current;
          while (next < order.length - 1) {
            const fdKey = order[next];
            const status = getFieldStepStatus(
              fdKey,
              merged,
              hasUserValue(getCreatePlanFieldValueRef.current?.(fdKey))
            );
            if (status === 'resolved' || status === 'user-provided') next++;
            else break;
          }
          return next;
        });
      },
      []
    );

    const processFlightUiActions = React.useCallback(
      async (llmResponse: LlmGenerateResponse, resolvedSessionId: string | null) => {
        const merged = mergeFlightTimelineRequest(llmResponse, resolvedSessionId);
        if (merged) {
          try {
            const built = await maestroChatService.buildFlightTimeline(merged);
            if (built.show_timeline) {
              setTimelineRendered(true);
              onFlightTimelineReady?.(built);
            }
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to build flight timeline';
            setError(msg);
          }
        }
        const mixerAction = llmResponse.ui_actions?.find((a) => a.type === 'open_media_mixer');
        if (mixerAction && mixerAction.type === 'open_media_mixer') {
          setMixerInitialAllocations(mixerAction.allocations_percent ?? {});
          setMixerReason(mixerAction.reason);
          setMixerOpen(true);
          onMediaMixerOpen?.(mixerAction);
        }
      },
      [onFlightTimelineReady, onMediaMixerOpen]
    );

    const makeStreamHandlers = React.useCallback(
      (targetMsgId: string) => ({
        onMeta: (meta: {
          session_id?: string;
          run_id?: string;
          thread_id?: string;
          assistant_message_id?: string;
        }) => {
          setStreamConnected(true);
          if (meta?.session_id) setSessionId(meta.session_id);
          const tid = meta?.run_id || meta?.thread_id;
          if (tid) {
            setRunId(tid);
            orchestratorThreadIdRef.current = tid;
          }
        },
        onContentDelta: ({ text }: { text: string }) => {
          if (!text) return;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === targetMsgId ? { ...msg, text: `${msg.text || ''}${text}` } : msg
            )
          );
          const p = currentSectionKeyRef.current;
          if (p && !SKIP_PHASES.has(p)) {
            appendPhaseOutput(p, text);
          }
        },
        onCotDelta: ({
          text,
          phase,
          step_id,
        }: {
          text: string;
          phase?: string;
          step_id?: string;
        }) => {
          if (!text) return;
          const raw = phase || '';
          if (raw && SKIP_PHASES.has(raw)) return;
          const key =
            raw || step_id
              ? sectionKeyForStepEvent({ phase: raw, step_id })
              : currentSectionKeyRef.current;
          if (!key || SKIP_PHASES.has(key)) return;
          if (key !== currentSectionKeyRef.current) {
            handleStepEvent({ phase: raw, step_id });
          }
          appendPhaseCot(key, text);
        },
        onToolCalled: (data: {
          run_id: string;
          iteration: number;
          tool_name: string;
          tool_args_summary: string;
          tool_call_id: string;
          phase?: string;
          step_id?: string;
        }) => {
          const toolLabel = progressLabel(data.tool_name);
          if (toolLabel) setProgressText(toolLabel);
          const ev = data as ToolCallEvent;
          const phase =
            ev.phase || ev.step_id
              ? sectionKeyForStepEvent({ phase: ev.phase, step_id: ev.step_id })
              : currentSectionKeyRef.current || '';
          if (SHOW_AGENT_TOOL_CALLS_UI) {
            setToolCalls((prev) => [
              ...prev,
              { ...data, status: 'pending' as const, result_summary: '', phase },
            ]);
            if (phase && !SKIP_PHASES.has(phase)) addPhaseToolCall(phase, data.tool_call_id);
          }
        },
        onToolResult: (data: {
          run_id: string;
          iteration: number;
          tool_name: string;
          tool_call_id: string;
          status: string;
          result_summary: string;
          ui_action?: { type: string; payload?: Record<string, unknown> } | null;
        }) => {
          if (SHOW_AGENT_TOOL_CALLS_UI) {
            setToolCalls((prev) =>
              prev.map((tc) =>
                tc.tool_call_id === data.tool_call_id
                  ? {
                      ...tc,
                      status: (data.status === 'error' ? 'error' : 'success') as
                        | 'success'
                        | 'error',
                      result_summary: data.result_summary,
                      ui_action: (data as ToolCallEvent).ui_action ?? undefined,
                    }
                  : tc
              )
            );
          }
          const uiAction = (data as ToolCallEvent).ui_action;
          if (
            uiAction?.type === 'apply_create_plan_fields' &&
            onApplyCreatePlanFields &&
            uiAction.payload &&
            'fields' in uiAction.payload &&
            consumeUiAction(uiAction as { action_id?: string }, appliedUiActionIdsRef.current)
          ) {
            const payload = uiAction.payload as {
              fields: Record<string, unknown>;
              field_results?: Record<string, MaestroFieldResult>;
            };
            const { fields, field_results } = payload;
            if (fields) {
              onApplyCreatePlanFields(fields, field_results);
              applyFieldResults(field_results);
            }
          }
        },
        onApprovalRequired: (raw: Record<string, unknown>) => {
          const ev = parseApprovalRequiredPayload(raw);
          setApprovalQueue((prev) => [
            ...prev,
            { key: `ap-${Date.now()}-${prev.length}`, data: ev },
          ]);
          // Auto-jump the mappings stepper to the field the gate is about (ORCH-256), whichever
          // kind of gate it is. ORCH-402: this was gated on `mdm_mapping` alone, so a user_input
          // question (Budget) raised a card without moving the active field — the chip stayed on
          // whatever the wizard last landed on while a different field's card was on screen.
          // `mappingsActiveStep` drives the chip highlight and the card together, so feeding it
          // from both gate shapes is what keeps them in step.
          const gateFdKey = ev.mapping?.fd_key ?? ev.user_input?.fd_key;
          if (gateFdKey) {
            // A gate for something that is not a Create Plan field (a free-form clarification)
            // has no chip to select: leave the active field alone rather than resetting it.
            const stepIdx = createPlanFieldOrderRef.current.indexOf(gateFdKey);
            if (stepIdx >= 0) setMappingsActiveStep(stepIdx);
          }
        },
        onProgress: ({ stage }: { stage: string }) => {
          // An unmapped stage keeps the previous line rather than showing a raw key.
          const stageLabel = progressLabel(stage);
          if (stageLabel) setProgressText(stageLabel);
        },
        onStatus: (data: { phase: string; [key: string]: unknown }) => {
          setStreamConnected(true);
          const p = typeof data.phase === 'string' ? data.phase : '';
          const stepId = typeof data.step_id === 'string' ? data.step_id : undefined;
          handleStepEvent({ phase: p, step_id: stepId });
        },
      }),
      [
        onApplyCreatePlanFields,
        applyFieldResults,
        appendPhaseCot,
        appendPhaseOutput,
        addPhaseToolCall,
        handleStepEvent,
        sectionKeyForStepEvent,
      ]
    );

    const makeParallelChatStreamHandlers = React.useCallback((targetMsgId: string) => {
      return {
        onMeta: (meta: { session_id?: string; run_id?: string; assistant_message_id?: string }) => {
          if (meta?.session_id) setSessionId(meta.session_id);
        },
        onContentDelta: ({ text }: { text: string }) => {
          if (!text) return;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === targetMsgId ? { ...msg, text: `${msg.text || ''}${text}` } : msg
            )
          );
        },
        onCotDelta: ({ text }: { text: string; phase?: string }) => {
          if (!text) return;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === targetMsgId
                ? { ...msg, streamingCot: `${msg.streamingCot || ''}${text}` }
                : msg
            )
          );
        },
      };
    }, []);

    const ORCHESTRATE_FROM_ATTACHED_BRIEF_PROMPT =
      'Use the media brief file attached to this plan (Create Media Plan draft) to run brief intake and continue the orchestrated planning workflow.';

    const executeGenerateStream = React.useCallback(
      async (
        llmPrompt: string,
        userDisplayText: string,
        options?: {
          parallelChat?: boolean;
          /** Keep accordion pipeline state (e.g. post–Create Plan brief review stub). */
          skipInitialPipelineReset?: boolean;
          orchestratorEntry?: 'brief_review_stub';
          orchestratorThreadIdOverride?: string;
        }
      ) => {
        const parallelChat = Boolean(options?.parallelChat && planningEntityId);
        const streamMode: 'chat' | 'plan' = parallelChat
          ? 'chat'
          : planningEntityId
            ? 'plan'
            : 'chat';

        if (parallelChat) {
          if (chatStreamBusy) return;
        } else if (planningEntityId) {
          if (workflowStreamBusy) return;
        } else if (chatStreamBusy) {
          return;
        }

        if (
          !parallelChat &&
          planningEntityId &&
          hasAttachedMediaBrief &&
          !options?.skipInitialPipelineReset
        ) {
          briefAutoRunStartedRef.current = true;
        }

        if (parallelChat) {
          setChatStreamBusy(true);
        } else if (planningEntityId) {
          setWorkflowStreamBusy(true);
          setProgressText('');
        } else {
          setChatStreamBusy(true);
        }

        setError(null);

        if (!parallelChat && !options?.skipInitialPipelineReset) {
          setApprovalQueue([]);
          setRunId(null);
          setLocalFieldResults({});
          localFieldResultsRef.current = {};
          setMappingsActiveStep(0);
          setBriefExtractionDone(false);
        }

        const showUserMessage = userDisplayText.trim().length > 0;
        setMessages((prev) =>
          showUserMessage
            ? [
                ...prev,
                {
                  id: `user-${Date.now()}`,
                  role: 'user' as const,
                  text: userDisplayText,
                  createdAt: new Date().toISOString(),
                  ...(parallelChat ? { sideChat: true as const } : {}),
                },
              ]
            : prev
        );

        const streamingAssistantId = `assistant-stream-${Date.now()}`;
        if (!parallelChat) {
          streamingAssistantIdRef.current = streamingAssistantId;
        }
        setMessages((prev) => [
          ...prev,
          {
            id: streamingAssistantId,
            role: 'assistant',
            text: '',
            createdAt: new Date().toISOString(),
            reasoning: [],
            toolEvents: [],
            ...(parallelChat ? { sideChat: true as const } : {}),
          },
        ]);

        if (!parallelChat && !options?.skipInitialPipelineReset) {
          setToolCalls([]);
          setOpenCompletedPhases(new Set());
          if (streamMode === 'plan') {
            const steps = planStepsRef.current;
            // Unlock the first row immediately — the run is starting now, so the
            // Briefing step shouldn't wait for the backend's first phase event to
            // stop looking locked (ORCH-273).
            setPhaseSections(unlockFirstStep(buildPlanPipelineFromSteps(steps)));
            const firstKey = steps.length ? steps[0].step_id : '';
            setCurrentSectionKey(firstKey);
            currentSectionKeyRef.current = firstKey;
          } else {
            setPhaseSections([]);
            setCurrentSectionKey('');
            currentSectionKeyRef.current = '';
          }
          setStreamConnected(false);
          lastStreamWasPlanRef.current = streamMode === 'plan';
          setActiveStreamPlan(streamMode === 'plan');
        } else if (!parallelChat) {
          lastStreamWasPlanRef.current = streamMode === 'plan';
          setActiveStreamPlan(streamMode === 'plan');
          setStreamConnected(false);
        }

        const humanApprovalMode: LlmGenerateRequest['human_approval_mode'] | undefined =
          streamMode === 'plan' ? 'always' : undefined;

        const sharedHandlers = parallelChat
          ? makeParallelChatStreamHandlers(streamingAssistantId)
          : makeStreamHandlers(streamingAssistantId);

        try {
          const response = await maestroChatService.generateStream(
            {
              prompt: llmPrompt,
              selected_models: selectedModelKey ? [selectedModelKey] : undefined,
              enable_web_search: true,
              session_id: sessionId ?? undefined,
              mode: streamMode,
              planning_entity_id: planningEntityId,
              timeline_rendered: timelineRendered,
              budget_allocation_context: budgetAllocationContext,
              human_approval_mode: humanApprovalMode,
              ...(streamMode === 'plan' && planningEntityId
                ? options?.orchestratorEntry === 'brief_review_stub'
                  ? {
                      continue_past_brief: true,
                      orchestrator_entry: 'brief_review_stub',
                      orchestrator_thread_id:
                        options.orchestratorThreadIdOverride ||
                        orchestratorThreadIdRef.current ||
                        undefined,
                    }
                  : {
                      continue_past_brief:
                        typeof resolveContinuePastBrief === 'function'
                          ? resolveContinuePastBrief()
                          : false,
                    }
                : {}),
            },
            {
              ...sharedHandlers,
              onAgentThinking: ({ message: thinkingMsg }) => {
                if (!thinkingMsg || parallelChat) return;
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingAssistantId
                      ? { ...msg, reasoning: [...(msg.reasoning || []), thinkingMsg] }
                      : msg
                  )
                );
              },
              ...(parallelChat
                ? {}
                : {
                    onPhaseStarted: (data: {
                      phase: string;
                      detail?: string;
                      step_id?: string;
                    }) => {
                      setStreamConnected(true);
                      handleStepEvent({ phase: data.phase, step_id: data.step_id });
                    },
                  }),
            }
          );

          const resolvedSessionId = response.session_id;
          setSessionId(resolvedSessionId);

          const llmResponse = response.llm_response as LlmGenerateResponse;
          const interrupted = Boolean(
            (llmResponse as { orchestrator_interrupted?: boolean }).orchestrator_interrupted
          );

          if (!interrupted) {
            const primaryOutput = pickPrimaryModelOutput(llmResponse);
            const assistantText = llmResponse.final_answer?.trim() || primaryOutput?.content || '';

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingAssistantId
                  ? {
                      ...msg,
                      text: assistantText,
                      streamingCot: undefined,
                      llmResponse,
                      createdAt: response.assistant_message.created_at || msg.createdAt,
                      reasoning: primaryOutput?.reasoning?.length
                        ? primaryOutput.reasoning
                        : undefined,
                      toolEvents: [],
                    }
                  : msg
              )
            );
            if (!parallelChat) {
              const intakeDone = Boolean(llmResponse.workflow_ui_handoff?.intake_complete);
              const briefReviewStubDone = Boolean(
                llmResponse.workflow_ui_handoff?.brief_review_complete
              );
              if (intakeDone) {
                applyIntakeHandoff(llmResponse);
                onBriefProcessed?.();
              } else if (briefReviewStubDone) {
                applyBriefReviewStubHandoff(llmResponse);
                onBriefProcessed?.();
              } else {
                finalizePlanPipeline();
              }
            }

            llmResponse.ui_actions?.forEach((a) => {
              if (a.type !== 'apply_create_plan_fields' || !a.payload?.fields) return;
              if (!consumeUiAction(a as { action_id?: string }, appliedUiActionIdsRef.current))
                return;
              const p = a.payload as {
                fields: Record<string, unknown>;
                field_results?: Record<string, MaestroFieldResult>;
              };
              onApplyCreatePlanFields?.(p.fields, p.field_results);
              applyFieldResults(p.field_results);
            });
            await processFlightUiActions(llmResponse, resolvedSessionId);
          } else {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingAssistantId ? { ...msg, streamingCot: undefined } : msg
              )
            );
            llmResponse.ui_actions?.forEach((a) => {
              if (a.type !== 'apply_create_plan_fields' || !a.payload?.fields) return;
              if (!consumeUiAction(a as { action_id?: string }, appliedUiActionIdsRef.current))
                return;
              const p = a.payload as {
                fields: Record<string, unknown>;
                field_results?: Record<string, MaestroFieldResult>;
              };
              onApplyCreatePlanFields?.(p.fields, p.field_results);
              applyFieldResults(p.field_results);
            });
            await processFlightUiActions(llmResponse, resolvedSessionId);
          }
        } catch (submitError) {
          setError(
            submitError instanceof Error
              ? submitError.message
              : 'Failed to get response from Maestro.'
          );
          // ORCH-265: a mid-run failure (tool error, LLM/API failure) can arrive after
          // apply_create_plan_fields tool results already coloured fields red, but before
          // the intake/review handoff that normally flips briefExtractionDone. Without this,
          // the extraction-quality banner never mounts and the user sees red fields with no
          // explanation at all — the general "any extraction failure" case this ticket exists for.
          if (!parallelChat && streamMode === 'plan') {
            setBriefExtractionDone(true);
          }
        } finally {
          if (!parallelChat) {
            setActiveStreamPlan(false);
          }
          if (parallelChat) {
            setChatStreamBusy(false);
          } else if (streamMode === 'plan') {
            setWorkflowStreamBusy(false);
          } else {
            setChatStreamBusy(false);
          }
        }
      },
      [
        chatStreamBusy,
        workflowStreamBusy,
        planningEntityId,
        selectedModelKey,
        sessionId,
        timelineRendered,
        budgetAllocationContext,
        resolveContinuePastBrief,
        makeStreamHandlers,
        makeParallelChatStreamHandlers,
        handleStepEvent,
        finalizePlanPipeline,
        applyIntakeHandoff,
        applyBriefReviewStubHandoff,
        processFlightUiActions,
        applyFieldResults,
        onApplyCreatePlanFields,
        onBriefProcessed,
        hasAttachedMediaBrief,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        openMediaMixer: (initialAllocations = {}, reason?: string) => {
          setMixerInitialAllocations(initialAllocations);
          setMixerReason(reason);
          setMixerOpen(true);
        },
        runBriefReviewAfterCreatePlan: async () => {
          const tid = orchestratorThreadIdRef.current;
          if (!planningEntityId || !tid) return;
          await executeGenerateStream('Post–Create Plan brief review workflow step.', '', {
            skipInitialPipelineReset: true,
            orchestratorEntry: 'brief_review_stub',
            orchestratorThreadIdOverride: tid,
          });
        },
      }),
      [executeGenerateStream, planningEntityId]
    );

    React.useEffect(() => {
      if (!hasAttachedMediaBrief) {
        briefAutoRunStartedRef.current = false;
      }
    }, [hasAttachedMediaBrief]);

    React.useEffect(() => {
      if (
        !planningEntityId ||
        !hasAttachedMediaBrief ||
        workflowStreamBusy ||
        briefAutoRunStartedRef.current
      )
        return;

      const timer = window.setTimeout(() => {
        void executeGenerateStream(ORCHESTRATE_FROM_ATTACHED_BRIEF_PROMPT, '');
      }, 1000);

      return () => window.clearTimeout(timer);
    }, [planningEntityId, hasAttachedMediaBrief, workflowStreamBusy, executeGenerateStream]);

    const handleOrchestratorResume = React.useCallback(
      async (detail: OrchestratorResumeDetail) => {
        if (!lastStreamWasPlanRef.current || !runId || !sessionId) return;
        const targetId = streamingAssistantIdRef.current;
        if (!targetId) return;

        setWorkflowStreamBusy(true);
        setProgressText('');
        setError(null);
        setStreamConnected(false);

        const resumeBody: Record<string, unknown> = {
          decision: detail.decision,
          comments: detail.comments ?? '',
        };
        if (detail.mapping) resumeBody.mapping = detail.mapping;
        if (detail.user_input !== undefined) resumeBody.user_input = detail.user_input;

        const sharedHandlers = makeStreamHandlers(targetId);

        try {
          const out = await maestroChatService.orchestrateResumeStream(
            {
              thread_id: runId,
              session_id: sessionId,
              resume: resumeBody,
            },
            {
              ...sharedHandlers,
              onPhaseStarted: (data: { phase: string; detail: string; step_id?: string }) => {
                handleStepEvent({ phase: data.phase, step_id: data.step_id });
              },
            }
          );

          const lr = out.llm_response as LlmGenerateResponse | undefined;
          const interrupted = Boolean(
            lr && (lr as { orchestrator_interrupted?: boolean }).orchestrator_interrupted
          );

          if (!interrupted && lr) {
            const assistantText =
              lr.final_answer?.trim() || out.assistant_message.content?.trim() || '';
            if (assistantText) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === targetId
                    ? { ...msg, text: assistantText, streamingCot: undefined, llmResponse: lr }
                    : msg
                )
              );
            } else {
              setMessages((prev) =>
                prev.map((msg) => (msg.id === targetId ? { ...msg, streamingCot: undefined } : msg))
              );
            }
            if (lr.workflow_ui_handoff?.intake_complete) {
              applyIntakeHandoff(lr);
              onBriefProcessed?.();
            } else if (lr.workflow_ui_handoff?.brief_review_complete) {
              applyBriefReviewStubHandoff(lr);
              onBriefProcessed?.();
            } else {
              finalizePlanPipeline();
            }
            lr.ui_actions?.forEach((a) => {
              if (a.type !== 'apply_create_plan_fields' || !a.payload?.fields) return;
              if (!consumeUiAction(a as { action_id?: string }, appliedUiActionIdsRef.current))
                return;
              const p = a.payload as {
                fields: Record<string, unknown>;
                field_results?: Record<string, MaestroFieldResult>;
              };
              onApplyCreatePlanFields?.(p.fields, p.field_results);
              applyFieldResults(p.field_results);
            });
            await processFlightUiActions(lr, sessionId);
          } else {
            setMessages((prev) =>
              prev.map((msg) => (msg.id === targetId ? { ...msg, streamingCot: undefined } : msg))
            );
            lr?.ui_actions?.forEach((a) => {
              if (a.type !== 'apply_create_plan_fields' || !a.payload?.fields) return;
              if (!consumeUiAction(a as { action_id?: string }, appliedUiActionIdsRef.current))
                return;
              const p = a.payload as {
                fields: Record<string, unknown>;
                field_results?: Record<string, MaestroFieldResult>;
              };
              onApplyCreatePlanFields?.(p.fields, p.field_results);
              applyFieldResults(p.field_results);
            });
            if (lr) await processFlightUiActions(lr, sessionId);
          }
        } catch (resumeErr) {
          setError(
            resumeErr instanceof Error ? resumeErr.message : 'Failed to resume planning run.'
          );
          // ORCH-265: same gap as executeGenerateStream's catch — a resume failure after
          // partial field results were already applied must not leave the extraction
          // banner permanently gated off.
          setBriefExtractionDone(true);
        } finally {
          setWorkflowStreamBusy(false);
          setActiveStreamPlan(false);
        }
      },
      [
        runId,
        sessionId,
        makeStreamHandlers,
        handleStepEvent,
        finalizePlanPipeline,
        applyIntakeHandoff,
        applyBriefReviewStubHandoff,
        processFlightUiActions,
        applyFieldResults,
        onApplyCreatePlanFields,
        onBriefProcessed,
      ]
    );

    /**
     * One frame around the panel: the border, without the theme card shadow on
     * top of it (ORCH-401, the outer nesting ORCH-403 deferred here). The theme's
     * MuiCard override sets `boxShadow` directly, so `elevation={0}` alone would
     * not remove it.
     */
    const baseCardSx: SxProps<Theme> = {
      boxShadow: 'none',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 420,
      minWidth: 0,
    };

    /** Workflow steps paired with their pipeline state, in sequence order. */
    const stepperRows = planSteps
      .map((step) => {
        const section = phaseSections.find((s) => s.key === step.step_id);
        return section ? { step, section } : null;
      })
      .filter((r): r is { step: PlanStepShape; section: PhaseSection } => r !== null);

    /**
     * In plan mode the workflow stepper carries the panel title in its header
     * row — "Maestro · Step N: Name Agent" (ORCH-426) — so the panel draws its
     * own title only when there is no stepper to carry it: outside plan mode,
     * and while the steps are still loading.
     */
    const titleInStepper = !!planningEntityId && stepperRows.length > 0;

    /**
     * Question / approval gates other than field mappings (e.g. the Budget
     * `user_input` card). In plan mode they belong to a step, so they render inside
     * `maestro-step-content`, beneath the step header (ORCH-401). Renders nothing
     * when the queue is empty, so the step gains no gap.
     */
    const renderApprovalCard = () => (
      <ApprovalCard
        items={approvalQueue.filter((i) => i.data.approval_phase !== 'mdm_mapping')}
        runId={runId}
        sessionId={sessionId}
        disabled={false}
        tenantId={tenantId}
        getFormFieldValue={(fdKey) => getCreatePlanFieldValue?.(fdKey) ?? undefined}
        onApplyCreatePlanFields={(fields) => onApplyCreatePlanFields?.(fields)}
        onResolved={(key) => setApprovalQueue((q) => q.filter((i) => i.key !== key))}
        onError={(msg) => setError(msg)}
        onOrchestratorResume={handleOrchestratorResume}
      />
    );

    /**
     * The plan-mode workflow stepper (ORCH-277): a dot rail across the top, the
     * viewed step's name as the heading, and only that step's content expanded.
     * Replaces the ten-row accordion this panel used to draw.
     */
    const renderPlanWorkflowChrome = () => {
      if (!planningEntityId) return null;
      const lastAssistant = messages.filter((m) => m.role === 'assistant' && !m.sideChat).at(-1);
      const msgText = lastAssistant?.text ?? '';
      /** Row the brief-intake mapping cards attach to (the form_intake step). */
      const briefRowKey = formIntakeStep(planSteps)?.step_id ?? '';

      const rows = stepperRows;

      /** The step the engine is running right now, or -1 between runs. */
      const runningIndex = rows.findIndex((r) => r.step.step_id === currentSectionKey);
      const pinnedIndex =
        pinnedStep && pinnedStep.atSection === currentSectionKey
          ? rows.findIndex((r) => r.step.step_id === pinnedStep.key)
          : -1;
      const firstUnlockedIndex = rows.findIndex((r) => !r.section.locked);
      /**
       * Which step is expanded: the one the user pinned by clicking its dot,
       * else the engine's current step, else the first reachable one. Before any
       * run every row is locked (ORCH-273), so this lands on step 1 and the
       * panel reads "Briefing — Ready, please attach a media brief".
       */
      const viewedIndex =
        pinnedIndex >= 0
          ? pinnedIndex
          : runningIndex >= 0
            ? runningIndex
            : firstUnlockedIndex >= 0
              ? firstUnlockedIndex
              : 0;

      const viewed = rows[viewedIndex];
      const section = viewed?.section;
      const sectionKey = viewed?.step.step_id ?? '';
      const isLastSection = viewedIndex === rows.length - 1;
      const isBriefingStep = !!sectionKey && sectionKey === briefRowKey;

      const sectionToolCalls = section
        ? toolCalls.filter((tc) => section.toolCallIds.includes(tc.tool_call_id))
        : [];
      const summaryText = section?.summaryMarkdown?.trim() || '';
      const displayText =
        summaryText ||
        section?.outputText ||
        (isLastSection && section?.completed && msgText ? msgText : '');
      /**
       * Open mapping gates on this step. Rendered only by the mappings wizard
       * below; counted here so the step knows it has something to show (ORCH-255).
       */
      const mappingItems = isBriefingStep
        ? approvalQueue.filter((i) => i.data.approval_phase === 'mdm_mapping')
        : [];
      const briefPassRunning = streamConnected || workflowStreamBusy;
      /**
       * One line of what Maestro is doing right now (ORCH-406) — replaces the
       * Thinking card's raw reasoning. Shown on the Briefing step for as long as
       * the run is active, from the moment it starts; gone once it finishes.
       */
      const progressLine =
        isBriefingStep && briefPassRunning ? progressText || PROGRESS_START_LABEL : '';

      /**
       * Where the panel stands, as plain muted text (no italics — ORCH-277).
       * Rendered on its own line for a step with nothing else to show. The
       * mappings wizard renders none of it (ORCH-403 removed its Back/Next row
       * and the "Running…" label that sat in it); while the Briefing step runs,
       * the workflow rail's pulsing dot in the step header is the live status.
       */
      const statusText = !section
        ? ''
        : section.locked
          ? isBriefingStep && !hasAttachedMediaBrief
            ? 'Ready — please attach a media brief.'
            : 'Unlocks when the previous workflow steps complete.'
          : isBriefingStep && !hasAttachedMediaBrief
            ? 'Ready — please attach a media brief.'
            : briefPassRunning
              ? 'Running…'
              : section.completed
                ? ''
                : 'Waiting to start…';

      /** True when the step has produced nothing yet, so the status line is all there is to say. */
      const hasStepContent = !!(
        section &&
        !section.locked &&
        (section.cot ||
          summaryText ||
          displayText ||
          sectionToolCalls.length ||
          mappingItems.length)
      );

      /**
       * Extraction quality banner (ORCH-265): outside the step's content so it
       * stays visible whichever step is expanded, and beneath the header group
       * so the panel still opens with its title (ORCH-426).
       */
      const extractionBanner =
        extractionSummary && extractionSummary.summaryType !== 'ok' ? (
          <DismissibleBanner
            severity={extractionSummary.summaryType === 'none' ? 'error' : 'warning'}
            sx={{ mt: HEADER_TO_CONTENT_GAP }}
            data-testid="extraction-summary-banner"
          >
            {extractionSummary.summaryType === 'none'
              ? "Maestro couldn't extract any fields from this brief. Please fill in the fields manually."
              : `Maestro filled ${extractionSummary.resolvedCount} of ${extractionSummary.totalFields} fields from this brief. The remaining fields will need to be filled in manually.`}
          </DismissibleBanner>
        ) : null;

      return (
        <Box sx={{ flexShrink: 0 }}>
          {stepsLoading && rows.length === 0 ? (
            <>
              {extractionBanner}
              <LinearProgress sx={{ my: 1 }} />
              {renderApprovalCard()}
            </>
          ) : rows.length === 0 ? (
            <>
              {extractionBanner}
              {renderApprovalCard()}
            </>
          ) : (
            <Box>
              <MaestroWorkflowStepper
                title={title}
                steps={rows.map((r) => ({
                  key: r.step.step_id,
                  label: r.step.label,
                  completed: r.section.completed,
                  locked: r.section.locked,
                }))}
                activeIndex={viewedIndex}
                runningIndex={runningIndex}
                onStepSelect={(idx) => {
                  const key = rows[idx]?.step.step_id;
                  setPinnedStep(key ? { key, atSection: currentSectionKey } : null);
                }}
              />

              {extractionBanner}

              {/* The viewed step's content — no nested border; the rail already
                  says which step this is (ORCH-277). A clearly larger gap than
                  the header-to-rail one, so the header group and the content
                  read as two groups (ORCH-426). */}
              <Box
                data-testid="maestro-step-content"
                sx={{ mt: extractionBanner ? 1.5 : HEADER_TO_CONTENT_GAP }}
              >
                {progressLine ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="maestro-progress-line"
                    aria-live="polite"
                    sx={{ ...maestroSurfaceSx, mb: 1 }}
                  >
                    {progressLine}
                  </Typography>
                ) : null}
                {/* Question cards (Budget …) belong to the step, so they sit under its header (ORCH-401). */}
                {renderApprovalCard()}
                {section && !section.locked ? (
                  <>
                    {/* Mappings wizard — the brief-intake step only (ORCH-256). */}
                    {isBriefingStep && (
                      <Box sx={{ mb: hasStepContent ? 1.5 : 0 }}>
                        <MaestroMappingsStepper
                          fieldResults={localFieldResults}
                          mappingApprovalItems={approvalQueue.filter(
                            (item) => item.data.approval_phase === 'mdm_mapping'
                          )}
                          activeStep={mappingsActiveStep}
                          onStepChange={setMappingsActiveStep}
                          getFormFieldValue={(fdKey) =>
                            getCreatePlanFieldValue?.(fdKey) ?? undefined
                          }
                          tenantId={tenantId}
                          onApplyCreatePlanFields={(fields) => onApplyCreatePlanFields?.(fields)}
                          onOrchestratorResume={handleOrchestratorResume}
                          onApprovalResolved={(key) =>
                            setApprovalQueue((q) => q.filter((i) => i.key !== key))
                          }
                          onError={(msg) => setError(msg)}
                          runId={runId}
                          sessionId={sessionId}
                          disabled={workflowStreamBusy || chatStreamBusy}
                          briefPassRunning={briefPassRunning}
                        />
                      </Box>
                    )}
                    {sectionToolCalls.length > 0 ? (
                      <Box>
                        <ToolCallPanel toolCalls={sectionToolCalls} />
                      </Box>
                    ) : null}
                    {displayText ? (
                      <Box
                        sx={{
                          ...maestroSurfaceSx,
                          mt: section.summaryMarkdown || sectionToolCalls.length ? 1 : 0,
                        }}
                      >
                        <MarkdownMessage content={displayText} />
                      </Box>
                    ) : null}
                    {/* The Briefing step's running state is the rail's pulsing dot (ORCH-403);
                        the wizard is its content, so no status line is added beneath it. */}
                    {statusText && !isBriefingStep && !hasStepContent ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        data-testid="maestro-step-status"
                        sx={maestroSurfaceSx}
                      >
                        {statusText}
                      </Typography>
                    ) : null}
                  </>
                ) : statusText ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="maestro-step-status"
                    sx={maestroSurfaceSx}
                  >
                    {statusText}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          )}
        </Box>
      );
    };

    const renderPhaseAccordions = (msgId: string, msgText: string) => {
      if (planningEntityId) return null;
      if (!phaseSections.length) return null;
      const lastAssistant = messages.filter((m) => m.role === 'assistant' && !m.sideChat).at(-1);
      if (lastAssistant?.id !== msgId) return null;

      return (
        <Box>
          {phaseSections.map((section, idx) => {
            const sectionToolCalls = toolCalls.filter((tc) =>
              section.toolCallIds.includes(tc.tool_call_id)
            );
            const isActive = !section.completed && !section.locked;
            const label = phaseLabel(section.key);
            const isLastSection = idx === phaseSections.length - 1;
            const displayText =
              section.outputText || (isLastSection && !isActive && msgText ? msgText : '');

            const expanded = isActive ? true : openCompletedPhases.has(section.key);

            return (
              <Accordion
                key={`${section.key}-${idx}`}
                expanded={expanded}
                onChange={(_, exp) => {
                  if (!section.completed) return;
                  setOpenCompletedPhases((prev) => {
                    const next = new Set(prev);
                    if (exp) next.add(section.key);
                    else next.delete(section.key);
                    return next;
                  });
                }}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  borderRadius: '4px !important',
                  mb: 0.5,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{
                    minHeight: 36,
                    py: 0,
                    px: 1.5,
                    '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
                  }}
                >
                  {section.completed ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                  {section.cot ? (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: '0.72rem',
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {sanitizeCoTDisplay(section.cot)}
                    </Box>
                  ) : null}
                  {sectionToolCalls.length > 0 ? (
                    <Box sx={{ mt: section.cot ? 0.75 : 0 }}>
                      <ToolCallPanel toolCalls={sectionToolCalls} />
                    </Box>
                  ) : null}
                  {displayText ? (
                    <Box sx={{ mt: section.cot || sectionToolCalls.length ? 1 : 0 }}>
                      <MarkdownMessage content={displayText} />
                    </Box>
                  ) : isActive && !section.cot && !sectionToolCalls.length ? (
                    <Typography variant="caption" color="text.secondary">
                      Running…
                    </Typography>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      );
    };

    return (
      <Card sx={(rootCardSx ? [baseCardSx, rootCardSx] : baseCardSx) as SxProps<Theme>}>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 2,
            gap: 1.5,
          }}
        >
          {titleInStepper ? null : (
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          )}

          {/* Outside plan mode there is no step to belong to, so gates stay at the root. */}
          {planningEntityId ? null : renderApprovalCard()}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {workflowStreamBusy || chatStreamBusy ? <LinearProgress /> : null}
          {renderPlanWorkflowChrome()}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: planningEntityId ? 0.5 : 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: planningEntityId ? 140 : 200,
                overflowY: 'auto',
                pr: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                ...(planningEntityId && !messages.some((m) => m.sideChat)
                  ? { flex: 0, minHeight: 0, overflowY: 'visible' as const }
                  : {}),
              }}
            >
              {(planningEntityId ? messages.filter((m) => m.sideChat) : messages).map((message) => {
                const isUser = message.role === 'user';
                const llmResponse = message.llmResponse;
                const modelOutputs = llmResponse?.model_outputs ?? [];
                const primaryFromLlm = llmResponse
                  ? pickPrimaryModelOutput(llmResponse)
                  : undefined;
                const isStructuredAssistant = !isUser && llmResponse && modelOutputs.length > 0;
                const isStreamingOnly = !isUser && !llmResponse;
                const isLastAssistant =
                  !isUser &&
                  messages.filter((m) => m.role === 'assistant' && !m.sideChat).at(-1)?.id ===
                    message.id;

                const accordions = message.sideChat
                  ? null
                  : isStreamingOnly || isLastAssistant
                    ? renderPhaseAccordions(message.id, message.text)
                    : null;

                return (
                  <Box
                    key={message.id}
                    sx={{
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                      maxWidth: '95%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: isUser ? brandColors.deepViolet : brandColors.offWhite,
                      color: isUser ? '#ffffff' : 'text.primary',
                      width: isUser ? undefined : '100%',
                    }}
                  >
                    {isUser ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.text}
                      </Typography>
                    ) : null}

                    {!isUser && (
                      <Stack spacing={0.75}>
                        {accordions ? (
                          accordions
                        ) : (
                          <>
                            {message.text ? <MarkdownMessage content={message.text} /> : null}
                            {isStructuredAssistant && primaryFromLlm?.content ? (
                              <MarkdownMessage content={primaryFromLlm.content} />
                            ) : null}
                          </>
                        )}
                        {!accordions && isStreamingOnly && !message.text ? (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: 'secondary.main' }}
                          >
                            {message.sideChat
                              ? 'Answering…'
                              : streamConnected
                                ? activeStreamPlan
                                  ? 'Running the workflow (this may take several minutes)…'
                                  : 'Generating…'
                                : 'Connecting…'}
                          </Typography>
                        ) : null}
                        {!accordions && isStreamingOnly && message.streamingCot ? (
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1,
                              maxHeight: 240,
                              overflow: 'auto',
                              fontFamily: 'ui-monospace, monospace',
                              fontSize: '0.75rem',
                              lineHeight: 1.45,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {sanitizeCoTDisplay(message.streamingCot)}
                          </Box>
                        ) : null}
                        {!accordions &&
                        isStreamingOnly &&
                        toolCalls.length > 0 &&
                        isLastAssistant &&
                        !message.sideChat ? (
                          <ToolCallPanel toolCalls={toolCalls} />
                        ) : null}
                        {!accordions &&
                        currentSectionKey &&
                        isLastAssistant &&
                        !message.sideChat ? (
                          <Chip
                            size="small"
                            label={`Phase: ${currentSectionKey.replace(/_/g, ' ')}`}
                            color="info"
                          />
                        ) : null}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </CardContent>

        <MediaMixerDrawer
          open={mixerOpen}
          onClose={() => setMixerOpen(false)}
          initialAllocations={mixerInitialAllocations}
          reason={mixerReason}
          onApply={(allocationsPercent) => {
            setBudgetAllocationContext({
              channel_allocations_percent: allocationsPercent,
              allocation_source: 'user_adjusted',
            });
          }}
        />
      </Card>
    );
  }
);

MaestroChatPanel.displayName = 'MaestroChatPanel';

export default MaestroChatPanel;
